<template>
  <b-modal
    id="photo-archive-picker-modal"
    v-model="isOpen"
    size="xl"
    title="Вставить из фотоархива"
    hide-footer
    scrollable
    @hidden="onHidden"
  >
    <b-row class="mb-2">
      <b-col md="5">
        <b-form-group label="Старт">
          <v-select
            v-model="startOption"
            :placeholder="'Начните вводить название'"
            :close-on-select="true"
            :clearable="true"
            :options="startOptions"
            :reduce="item => item"
            label="label"
            class="select"
            @search="(search, loading) => getStarts(search, loading)"
            @input="onStartChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group label="Год">
          <b-form-select
            v-model="filters.year"
            @change="onYearChange"
          >
            <b-form-select-option value="">
              Все
            </b-form-select-option>
            <b-form-select-option
              v-for="year in yearOptions"
              :key="year"
              :value="year"
            >
              {{ year }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Альбом">
          <b-form-select
            v-model="filters.album_id"
            :disabled="!activeStartId"
            @change="reloadPhotos"
          >
            <b-form-select-option value="">
              Все
            </b-form-select-option>
            <b-form-select-option
              v-for="album in albums"
              :key="album.id"
              :value="String(album.id)"
            >
              {{ album.name }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Тип тега">
          <b-form-select
            v-model="filters.tag_type"
            :disabled="!activeStartId"
            @change="reloadPhotos"
          >
            <b-form-select-option value="">
              Все
            </b-form-select-option>
            <b-form-select-option value="bib">
              Номер
            </b-form-select-option>
            <b-form-select-option value="custom">
              Общий
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-2">
      <b-col md="6">
        <b-form-input
          v-model="filters.tag_name"
          :disabled="!activeStartId"
          placeholder="Поиск по тегу"
          @keyup.enter="reloadPhotos"
        />
      </b-col>
      <b-col md="6" class="d-flex align-items-end">
        <b-button
          variant="primary"
          class="mr-1"
          :disabled="!activeStartId || isLoading"
          @click="reloadPhotos"
        >
          Найти
        </b-button>
        <b-button
          variant="outline-primary"
          :disabled="!canInsertGallery"
          @click="insertGalleryShortcode"
        >
          Вставить галерею по фильтру
        </b-button>
      </b-col>
    </b-row>

    <div
      v-if="!activeStartId"
      class="text-muted text-center py-4"
    >
      Выберите старт
    </div>

    <div
      v-else-if="isLoading"
      class="text-center py-4"
    >
      <b-spinner />
    </div>

    <div
      v-else
      class="picker-grid"
    >
      <button
        v-for="photo in photos"
        :key="photo.id"
        type="button"
        class="picker-grid__item"
        @click="insertPhotoShortcode(photo)"
      >
        <img
          :src="photo.file.fullPath"
          alt=""
        >
        <span
          v-if="photo.album"
          class="picker-grid__caption"
        >
          {{ photo.album.name }}
        </span>
      </button>
    </div>

    <div
      v-if="activeStartId && !isLoading && photos.length === 0"
      class="text-muted text-center py-3"
    >
      Фото не найдены
    </div>
  </b-modal>
</template>

<script>
import {
  BButton,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import moment from 'moment'
import { eventBus } from '@/libs/event-bus'
import crudModule from '@/libs/crud_module'
import { buildPhotoShortcode, describePhotoShortcode } from '@/utils/photo-shortcode'

const startModule = crudModule({
  entity_name: 'start',
})

export default {
  name: 'PhotoArchivePickerModal',
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BButton,
    BSpinner,
    vSelect,
  },
  data() {
    const currentYear = new Date().getFullYear()
    const yearOptions = []
    for (let year = currentYear; year >= currentYear - 8; year -= 1) {
      yearOptions.push(String(year))
    }

    return {
      isOpen: false,
      quill: null,
      range: null,
      contextStartId: null,
      requireStartPick: false,
      includeStartInShortcode: false,
      startOption: null,
      startOptions: [],
      yearOptions,
      albums: [],
      photos: [],
      isLoading: false,
      debounce: null,
      filters: {
        year: '',
        album_id: '',
        tag_type: '',
        tag_name: '',
      },
    }
  },
  computed: {
    activeStartId() {
      return this.startOption?.id || null
    },
    canInsertGallery() {
      return !!(this.activeStartId && (this.filters.album_id || (this.filters.tag_type && this.filters.tag_name)))
    },
  },
  created() {
    eventBus.$on('open-photo-archive-picker', this.open)
  },
  beforeDestroy() {
    eventBus.$off('open-photo-archive-picker', this.open)
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format('DD.MM.YYYY') : ''
    },
    mapStartOption(row) {
      return {
        ...row,
        label: `${row.name} (${this.formatDate(row.start_date)})`,
      }
    },
    shouldIncludeStartInShortcode() {
      if (this.includeStartInShortcode) {
        return true
      }
      if (this.contextStartId && Number(this.activeStartId) !== Number(this.contextStartId)) {
        return true
      }
      return false
    },
    open(payload) {
      this.quill = payload.quill
      this.range = payload.range
      this.contextStartId = payload.startId ? +payload.startId : null
      this.requireStartPick = !!payload.requireStartPick
      this.includeStartInShortcode = !!payload.includeStartInShortcode
      this.filters = { year: '', album_id: '', tag_type: '', tag_name: '' }
      this.photos = []
      this.isOpen = true

      if (this.contextStartId) {
        this.initializePicker()
      } else {
        this.startOption = null
        this.albums = []
        this.loadInitialStarts()
      }
    },
    async initializePicker() {
      await this.loadStartById(this.contextStartId)
    },
    onHidden() {
      this.quill = null
      this.range = null
    },
    getStarts(search, loading) {
      clearTimeout(this.debounce)
      if (!search?.length) {
        this.loadInitialStarts()
        loading(false)
        return
      }
      loading(true)
      this.debounce = setTimeout(async () => {
        try {
          await this.fetchStartOptions({ filterText: search })
        } catch (error) {
          console.error(error)
        } finally {
          loading(false)
        }
      }, 400)
    },
    async loadInitialStarts() {
      try {
        await this.fetchStartOptions()
      } catch (error) {
        console.error(error)
      }
    },
    async fetchStartOptions(extraParams = {}) {
      const client = this.$http || this.$axios
      const params = {
        maxResultCount: 30,
        ...extraParams,
      }
      if (this.filters.year) {
        params.year = this.filters.year
      }
      const { data } = await client.get('photo-archive/starts', { params })
      this.startOptions = (data?.rows || []).map(row => this.mapStartOption(row))
    },
    async onYearChange() {
      this.startOption = null
      this.albums = []
      this.photos = []
      await this.loadInitialStarts()
    },
    async loadStartById(startId) {
      try {
        const result = await startModule.fetchItem(`admin/${startId}`)
        const startData = result.data.start_data || result.data
        this.startOption = this.mapStartOption(startData)
        await this.loadAlbums()
        await this.reloadPhotos()
        await this.loadInitialStarts()
        if (this.startOption && !this.startOptions.some(item => item.id === this.startOption.id)) {
          this.startOptions = [this.startOption, ...this.startOptions]
        }
      } catch (error) {
        console.error('Не удалось загрузить старт для фотоархива:', error)
      }
    },
    async onStartChange(start) {
      this.filters.album_id = ''
      this.filters.tag_type = ''
      this.filters.tag_name = ''
      this.photos = []
      if (!start?.id) {
        this.albums = []
        return
      }
      await this.loadAlbums()
      await this.reloadPhotos()
    },
    async loadAlbums() {
      const client = this.$http || this.$axios
      const { data } = await client.get('photo-archive/starts', {
        params: { start_id: this.activeStartId, maxResultCount: 1 },
      })
      this.albums = data?.rows?.[0]?.albums || []
    },
    buildPhotoParams() {
      const params = { skipCount: 0, maxResultCount: 60 }
      if (this.filters.album_id) params.album_id = this.filters.album_id
      if (this.filters.tag_type) params.tag_type = this.filters.tag_type
      if (this.filters.tag_name) params.tag_name = this.filters.tag_name
      return params
    },
    async reloadPhotos() {
      if (!this.activeStartId) return
      this.isLoading = true
      try {
        const client = this.$http || this.$axios
        const { data } = await client.get(`photo-archive/starts/${this.activeStartId}/photos`, {
          params: this.buildPhotoParams(),
        })
        this.photos = data?.rows || []
      } finally {
        this.isLoading = false
      }
    },
    insertShortcodeEmbed(meta) {
      if (!this.quill || !meta?.shortcode) return
      const index = this.range?.index ?? this.quill.getLength()
      this.quill.insertEmbed(index, 'photo-shortcode', meta, 'user')
      this.quill.setSelection(index + 1, 0, 'user')
      this.isOpen = false
    },
    insertPhotoShortcode(photo) {
      const shortcode = buildPhotoShortcode({
        kind: 'photo',
        photoId: photo.id,
        startId: this.activeStartId,
        includeStart: this.shouldIncludeStartInShortcode(),
      })
      this.insertShortcodeEmbed({
        shortcode,
        kind: 'photo',
        previewUrl: photo.file?.fullPath || '',
        label: `Фото #${photo.id}`,
      })
    },
    insertGalleryShortcode() {
      if (!this.canInsertGallery) return

      let galleryType = this.filters.tag_type === 'bib' ? 'bib' : 'custom'
      let galleryValue = this.filters.tag_name

      if (this.filters.album_id) {
        galleryType = 'album'
        galleryValue = this.filters.album_id
      }

      const shortcode = buildPhotoShortcode({
        kind: 'photos',
        galleryType,
        galleryValue,
        startId: this.activeStartId,
        includeStart: this.shouldIncludeStartInShortcode(),
      })
      this.insertShortcodeEmbed({
        shortcode,
        kind: 'photos',
        label: describePhotoShortcode(shortcode),
      })
    },
  },
}
</script>

<style scoped lang="scss">
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  max-height: 50vh;
  overflow-y: auto;
}

.picker-grid__item {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 6px;
  background: #fff;
  cursor: pointer;
  text-align: left;

  img {
    width: 100%;
    height: 110px;
    object-fit: cover;
    border-radius: 4px;
  }

  &:hover {
    border-color: #7367f0;
  }
}

.picker-grid__caption {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #6e6b7b;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
